// src/components/LanguageDropdown.tsx
import { FC, useState, useRef, useEffect } from 'react';
import './LanguageDropdown.css'; // Importer le CSS pour les styles personnalisés
import { useAppSelector } from '../../store/store'; // Utiliser le hook personnalisé pour useSelector
import { useDispatch } from 'react-redux';
import { changeLanguage } from '../../store/actions/languageActions'; // Importer la thunk action changeLanguage
import { IStateLanguage } from 'store/store.model';
import { useTranslation } from 'react-i18next';

// Définir le type pour l'objet des langues
interface Languages {
  [key: string]: string;
}

// Définir les props pour le composant (si besoin d'extension future)
interface LanguageDropdownProps {
  defaultLanguage?: string; // Prop optionnelle pour définir une langue par défaut
}

const LanguageDropdown: FC<LanguageDropdownProps> = () => {
  const dispatch = useDispatch();
  const stateLanguage = useAppSelector((state) => state.language.language); // Sélectionner la langue actuelle du store Redux
  const { i18n } = useTranslation();

  // Définir les langues disponibles et leurs noms d'affichage
  const languages: Languages = {
    gb: 'English', // Utilisez 'gb' pour le drapeau anglais
    fr: 'French',
    es: 'Spanish',
    de: 'German',
    it: 'Italian',
    jp: 'Japanese',
    // Ajouter plus de langues si nécessaire
  };

  // Mapping entre les codes de drapeau et les codes i18n
  const languageCodes: { [key: string]: string } = {
    gb: 'en',
    fr: 'fr',
    es: 'es',
    de: 'de',
    it: 'it',
    jp: 'jp',
  };

  // Inverse mapping to find the flag code from the selected language
  const flagCodes: { [key: string]: string } = Object.fromEntries(
    Object.entries(languageCodes).map(([flag, lang]) => [lang, flag])
  );

  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Gestion du changement de langue
  const handleChangeLanguage = (code: string) => {
    const i18nCode = languageCodes[code];
    const language: IStateLanguage = { language: i18nCode };
    changeLanguage(language, dispatch); // Utiliser la thunk action pour changer la langue
    setIsDropdownOpen(false); // Ferme la liste après la sélection
  };

  useEffect(() => {
    const savedLang = localStorage.getItem('app_lang');
    if (savedLang && savedLang !== i18n.resolvedLanguage) {
      changeLanguage({ language: savedLang }, dispatch);
    } else if (!savedLang && i18n.resolvedLanguage && i18n.resolvedLanguage !== stateLanguage) {
      changeLanguage({ language: i18n.resolvedLanguage }, dispatch);
    }
  }, [dispatch, i18n.resolvedLanguage, stateLanguage]);

  // Gestion du clic en dehors pour fermer le dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fonction pour générer la classe des drapeaux
  const getFlagClass = (code: string): string => `fi fi-${code}`;

  // Find the flag code from the selected language
  const selectedFlagCode = flagCodes[i18n.resolvedLanguage || 'en'] || 'gb'; // Default to 'gb' if not found

  return (
    <div className="mx-2 language-dropdown" ref={dropdownRef}>
      <div
        className="selected-flag-container"
        onClick={() => setIsDropdownOpen((prev) => !prev)} // Toggle la liste au clic
      >
        <span className={`selected-flag ${getFlagClass(selectedFlagCode)}`} />
      </div>

      {isDropdownOpen && (
        <ul className="dropdown-list">
          {Object.entries(languages).map(([code, language]) => (
            <li key={code} className="dropdown-flag-item" onClick={() => handleChangeLanguage(code)}>
              <span className={`dropdown-flag ${getFlagClass(code)}`} /> {language}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LanguageDropdown;
